import { Entity, formatTicker } from '@api';
import { ButtonGroup } from '@design/components/button-group/ButtonGroup';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { useElementSize } from '@helpers';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import {
  NEW_YORK_TIMEZONE,
  usePriceMarketReactionData,
} from '@terminal/hooks/use-price-market-reaction-data/usePriceMarketReactionData';
import { EarningsMarketReactionChart } from '@terminal/modules/market-reaction-charts/earnings-market-reaction-chart/EarningsMarketReactionChart';
import { useGetEarningById } from '@terminal/react-query-hooks/earnings/useGetEarningById';
import { useScenarios } from '@terminal/react-query-hooks/scenarios/useScenarios';
import { ChartEmptyStates } from '@terminal/views/dashboard/calendar-widget/calendar-event-drawer/drawer-content/earnings-event-drawer-content/earnings-chart/ChartEmptyStates';
import { ExpectationReported } from '@terminal/views/dashboard/calendar-widget/calendar-event-drawer/drawer-content/earnings-event-drawer-content/expectation-reported/ExpectationReported';
import { Expectation } from '@terminal/views/dashboard/calendar-widget/calendar-event-drawer/drawer-content/earnings-event-drawer-content/types';
import { earningsDataForEmptyState } from '@terminal/widgets/earnings-insight-drawer/earnings-insight-widget/earnings-preview/components/eps-expectations-section/utils';
import clsx from 'clsx';
import { format, isAfter, isBefore, subMonths } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HistoricalReaction } from './historical-reaction/HistoricalReaction';
import styles from './MarketReactionSection.module.scss';

interface MarketReactionSectionProps {
  entity: Entity;
  earningsId: string;
  reportingDate: string;
}

const START_HORIZON = '-2d';
const END_HORIZON = '5d';

export const MarketReactionSection = ({
  entity,
  earningsId,
  reportingDate,
}: MarketReactionSectionProps) => {
  const { t } = useTranslation(['earnings']);
  const chartWrapperRef = useRef(null);
  const { width } = useElementSize(chartWrapperRef);
  const buttonOptions = useMemo(
    () => [
      t('earnings:earningsInsight.drawer.earningsRecap.marketReaction'),
      t('earnings:earningsInsight.drawer.earningsRecap.historicalReaction'),
    ],
    [t]
  );
  const [selectedButtonIndex, setSelectedButtonIndex] = useState(0);
  const isMarketReactionSelected = selectedButtonIndex === 0;
  const lastHistoricalRequestKeyRef = useRef<string | null>(null);
  const {
    sendMessage: sendHistoricalScenarios,
    data: historicalScenariosData,
    isLoading: isHistoricalScenariosLoading,
    error: hasHistoricalScenariosError,
  } = useScenarios();

  const {
    data: earningsData,
    isLoading: isEarningsLoading,
    isError: isEarningsError,
    error: earningsError,
  } = useGetEarningById(earningsId);

  const {
    priceData,
    fromDateEST,
    toDateEST,
    lastProportionChange,
    lastKnownPrice,
    prevCloseData,
    lastKnownDate,
    isLoading: isMarketReactionDataLoading,
  } = usePriceMarketReactionData({
    ticker: entity?.subscribable_ticker ?? '',
    entityTag: entity.tag,
    reportingDate,
  });

  const hasEpsData =
    !isEarningsLoading && earningsData?.eps?.surprise !== undefined;
  const nowEST = toZonedTime(new Date(), NEW_YORK_TIMEZONE);
  const isWindowComplete = toDateEST ? isAfter(nowEST, toDateEST) : false;
  const formattedEndDate = toDateEST ? format(toDateEST, 'MMM d') : undefined;
  const hasError = isEarningsError || hasHistoricalScenariosError;
  const errorMessage = earningsError?.message;
  const epsSurprise = earningsData?.eps?.surprise;
  const expectation =
    epsSurprise !== undefined
      ? epsSurprise >= 0
        ? Expectation.Beat
        : Expectation.Miss
      : undefined;
  // close === 0 is a missing-data sentinel from price history
  const has2DayData =
    !!toDateEST &&
    priceData.some(({ value }) => Number.isFinite(value) && value !== 0);
  const isDataLoaded = !isEarningsLoading && !isMarketReactionDataLoading;
  const isOldReportingDate = isBefore(
    new Date(reportingDate),
    subMonths(new Date(), 1)
  );
  const shouldDeferHistoricalRequest =
    isOldReportingDate && isMarketReactionDataLoading;
  const shouldHideUnavailableOldMarketReaction =
    isDataLoaded && !has2DayData && isOldReportingDate;

  useEffect(() => {
    if (
      !earningsData ||
      !expectation ||
      isEarningsLoading ||
      shouldDeferHistoricalRequest ||
      shouldHideUnavailableOldMarketReaction
    ) {
      return;
    }

    const condition = expectation === Expectation.Beat ? 'above' : 'below';
    const expectedValue = expectation === Expectation.Beat ? 0.01 : -0.01;

    const payload = {
      entities: [{ tag: entity.tag }],
      conditions: [
        {
          condition: condition,
          snake: `${entity.tag}.surprise_absolute_eps`,
          value: epsSurprise ?? expectedValue,
        },
      ],
      start_horizon: START_HORIZON,
      end_horizon: END_HORIZON,
      skip_report: true,
      non_overlapping: true,
    };

    const historicalRequestKey = JSON.stringify({
      earningsId,
      entityTag: entity.tag,
      expectation,
      epsSurprise: epsSurprise ?? expectedValue,
    });

    if (lastHistoricalRequestKeyRef.current === historicalRequestKey) {
      return;
    }

    lastHistoricalRequestKeyRef.current = historicalRequestKey;
    sendHistoricalScenarios(payload);
  }, [
    earningsData,
    earningsId,
    entity.tag,
    epsSurprise,
    expectation,
    isEarningsLoading,
    sendHistoricalScenarios,
    shouldDeferHistoricalRequest,
    shouldHideUnavailableOldMarketReaction,
  ]);

  const hasHistoricalData =
    !!earningsData && (historicalScenariosData[0]?.episodes?.length ?? 0) > 0;

  const skeleton = (
    <SkeletonLoader
      areas={[
        {
          styles: {
            width: '100%',
            height: '300px',
            borderRadius: '8px',
          },
        },
      ]}
    />
  );

  const historicalEmptyState =
    expectation && earningsData ? (
      <>
        <ExpectationReported
          earning={earningsData.eps}
          currency={earningsData.currency}
        />
        <ChartEmptyStates
          earningsData={earningsDataForEmptyState(
            earningsData,
            entity.name_short || entity.name,
            entity.ticker
          )}
          expectation={expectation}
          scenario={null}
        />
      </>
    ) : null;

  const renderContent = () => {
    if (isEarningsError) {
      return (
        <div data-testid="error-message">
          {errorMessage ?? t('earnings:empty.description')}
        </div>
      );
    }

    if (!isDataLoaded) {
      return skeleton;
    }

    if (shouldHideUnavailableOldMarketReaction) {
      return null;
    }

    if (hasHistoricalScenariosError) {
      return (
        <div data-testid="error-message">
          {errorMessage ?? t('earnings:empty.description')}
        </div>
      );
    }

    if (has2DayData) {
      if (hasEpsData && !isMarketReactionSelected) {
        if (isHistoricalScenariosLoading) return skeleton;
        if (!hasHistoricalData) {
          return historicalEmptyState;
        }
        return (
          <HistoricalReaction
            entity={entity}
            earningsData={earningsData!}
            scenariosData={historicalScenariosData}
            width={width}
          />
        );
      }

      return (
        <ErrorBoundaryWrapper>
          <EarningsMarketReactionChart
            fromDateEST={fromDateEST}
            toDateEST={toDateEST}
            line={{
              data: priceData,
              label: formatTicker(entity),
              prevCloseData,
              lastProportionChange,
              lastKnownPrice,
              lastKnownDate,
              subject: { type: 'entity', data: entity },
            }}
            entity={entity}
          />
        </ErrorBoundaryWrapper>
      );
    }

    if (hasEpsData) {
      if (isHistoricalScenariosLoading) return skeleton;
      if (hasHistoricalData) {
        return (
          <HistoricalReaction
            entity={entity}
            earningsData={earningsData!}
            scenariosData={historicalScenariosData}
            width={width}
          />
        );
      }
      return historicalEmptyState;
    }

    return null;
  };

  const content = renderContent();

  if (content === null) {
    return null;
  }

  const showTabs = has2DayData && hasEpsData;
  const showHistoricalTitle =
    isDataLoaded && !has2DayData && hasEpsData && !hasError;

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        {showTabs && (
          <div
            className={clsx(styles.styledButtonGroup, {
              [styles.headerWrapper]: isMarketReactionSelected,
            })}
          >
            <ButtonGroup
              labels={buttonOptions}
              activeIndex={selectedButtonIndex}
              onClick={setSelectedButtonIndex}
            />
            {isMarketReactionSelected && !!toDateEST && (
              <div className={clsx(styles.windowDescription)}>
                <svg
                  width={12}
                  height={12}
                  viewBox="0 0 40 40"
                  fill={
                    isWindowComplete
                      ? 'var(--icon-soft)'
                      : 'var(--icon-success)'
                  }
                >
                  <circle cx="20" cy="20" r="12" />
                </svg>
                <span className={styles.windowTitleText}>
                  {isWindowComplete
                    ? t('insights:catalyst.drawer.windowComplete')
                    : t('insights:catalyst.drawer.liveWindow')}
                </span>
                <span className={styles.windowTitleText}>
                  {isWindowComplete
                    ? t('insights:catalyst.drawer.finalAsOfDateClose', {
                        date: formattedEndDate,
                      })
                    : t('insights:catalyst.drawer.endsAtClose', {
                        date: formattedEndDate,
                      })}
                </span>
              </div>
            )}
          </div>
        )}
        {showHistoricalTitle && (
          <h2 className={styles.title}>
            {t(
              'earnings:earningsInsight.drawer.earningsRecap.historicalReaction'
            )}
          </h2>
        )}
      </div>
      <div className={styles.contentWrapper} ref={chartWrapperRef}>
        {content}
      </div>
    </div>
  );
};
